import React, { useEffect, useState } from 'react'

import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Divider,
  Flex,
  Input,
  Link,
  Stack,
  Text,
  useMediaQuery,
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { signInWithRedirect } from 'aws-amplify/auth'
import { useForm } from 'react-hook-form'
import { useNavigate, useLocation } from 'react-router'
import MirohaLogoFull from 'src/assets/image/miroha-logo-full.svg'
import TopImage from 'src/assets/image/top-image.svg'
import { layouts } from 'src/assets/layouts'
import { Message } from 'src/components/__legacy__/message/message'
import { Linkify } from 'src/components/Linkify/Linkify'
import { MicrosoftLogo } from 'src/components/svg'
import { SSOAccountActivatedDialog } from 'src/features/auth/components/SSOAccountActivatedDialog/SSOAccountActivatedDialog'
import { useShowSSOLoginButton } from 'src/features/auth/hooks/useShowSSOLoginButton'
import { useSSOAccountActivated } from 'src/features/auth/hooks/useSSOAccountActivated'
import { useCurrentVersion } from 'src/features/misc/hooks/useCurrentVersion'
import { OnSubmit } from 'src/legacy_modules/auth/login/hooks/use-login'
import { useFetch as useFetchInformation } from 'src/legacy_modules/information/use-fetch'
import { routes } from 'src/legacy_modules/routes'
import * as yup from 'yup'

const largeScreen = `min-width: ${layouts.tabletLandscapeWithMin}`

const SSOLoginButton: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [ssoError, setSsoError] = useState<string | null>(
    (location.state as { ssoError?: string } | null)?.ssoError ?? null,
  )

  useEffect(() => {
    if ((location.state as { ssoError?: string } | null)?.ssoError) {
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: {} },
      )
    }
  }, [location, navigate])

  const handleSSOLogin = async () => {
    setSsoError(null)
    try {
      await signInWithRedirect({
        provider: { custom: import.meta.env.VITE_COGNITO_IDP_NAME },
      })
    } catch {
      setSsoError('SSOログインに失敗しました。再度お試しください。')
    }
  }

  return (
    <Flex flexDirection="column" alignItems="center" gap={2}>
      <Button
        variant="outline"
        borderColor="black"
        color="black"
        _hover={{ bg: 'blackAlpha.50' }}
        onClick={handleSSOLogin}
        w="220px"
        leftIcon={<MicrosoftLogo />}
      >
        Microsoftでサインイン
      </Button>
      {ssoError && <Message type="error" message={ssoError} />}
    </Flex>
  )
}

type Props = {
  errorMessage: string
  requesting: boolean
  onSubmit: OnSubmit
}

const validationSchema = yup.object().shape({
  email: yup.string().required(),
  password: yup.string().required(),
})

type FormValues = yup.InferType<typeof validationSchema>

export const Login: React.FC<Props> = ({
  errorMessage,
  requesting,
  onSubmit: submit,
}) => {
  const navigate = useNavigate()
  const { register, handleSubmit, formState, trigger } = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  })
  const { isValid, touchedFields, errors } = formState

  const onSubmit = (value: FormValues) => {
    if (requesting) return
    if (!isValid) return

    submit(value)
  }

  const { informationList } = useFetchInformation()
  const hasInfo = informationList.length > 0

  const [isLargeScreen] = useMediaQuery(`(${largeScreen})`)

  const { currentVersion } = useCurrentVersion()

  const {
    isOpen: isSSOAccountActivatedDialogOpen,
    onClose: onCloseSSOAccountActivatedDialog,
  } = useSSOAccountActivated()
  const showSSOLoginButton = useShowSSOLoginButton()

  useEffect(() => {
    trigger()
  }, [trigger])

  return (
    <Flex
      flexDirection={isLargeScreen ? 'row' : 'column'}
      w="100%"
      h="100%"
      alignItems="center"
      justifyContent="center"
    >
      <Flex
        flex={1}
        h="100%"
        w="100%"
        flexDirection="column"
        alignItems="center"
        bg={isLargeScreen ? '#F0F4FE' : 'white'}
      >
        <Box
          as="form"
          flex={1}
          w="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <Box w="100%" maxW="420px">
            <Flex flexDirection="column" alignItems="center" gap={8}>
              <img src={MirohaLogoFull} alt="MiROHA icon" />

              <Text fontWeight="bold" fontSize="xl">
                ログイン
              </Text>
            </Flex>
          </Box>

          <Box w="100%" maxW="420px" mt={8}>
            <Flex flexDirection="column" gap={6} w="full">
              <Box>
                <Text fontWeight="bold" fontSize="sm" mb={2}>
                  メールアドレス
                </Text>
                <Input
                  type="email"
                  placeholder="メールアドレス"
                  bg="white"
                  {...register('email')}
                />
                {errors.email && touchedFields.email && (
                  <Box mt={1}>
                    <Message
                      type="error"
                      message="メールアドレスを入力してください"
                    />
                  </Box>
                )}
              </Box>

              <Box>
                <Text fontWeight="bold" fontSize="sm" mb={2}>
                  パスワード
                </Text>
                <Input
                  type="password"
                  placeholder="パスワード"
                  bg="white"
                  {...register('password')}
                />
                {errors.password && touchedFields.password && (
                  <Box mt={1}>
                    <Message
                      type="error"
                      message="パスワードを入力してください"
                    />
                  </Box>
                )}
              </Box>

              <Button type="submit" w="220px" mx="auto" isLoading={requesting}>
                ログイン
              </Button>

              {errorMessage && <Message type="error" message={errorMessage} />}

              <Flex justifyContent="center">
                <Link
                  color="blue.500"
                  onClick={() => navigate(routes.forgetPassword)}
                  cursor="pointer"
                >
                  パスワードをお忘れですか？
                </Link>
              </Flex>

              {showSSOLoginButton && (
                <>
                  <Flex alignItems="center" gap={3}>
                    <Divider borderColor="gray.300" />
                    <Text
                      whiteSpace="nowrap"
                      fontWeight="bold"
                      fontSize="sm"
                      color="gray.600"
                    >
                      または
                    </Text>
                    <Divider borderColor="gray.300" />
                  </Flex>

                  <SSOLoginButton />
                </>
              )}
            </Flex>
          </Box>
        </Box>
      </Flex>

      <Box
        w="100%"
        display="flex"
        justifyContent="center"
        bg="white"
        h={isLargeScreen ? '100%' : hasInfo ? '250px' : undefined}
        flex={isLargeScreen ? 1 : undefined}
        backgroundImage={
          isLargeScreen
            ? hasInfo
              ? `linear-gradient(rgba(256, 256, 256, 0.7), rgba(256, 256, 256, 0.7)), url(${TopImage})`
              : `url(${TopImage})`
            : undefined
        }
        backgroundRepeat={isLargeScreen ? 'no-repeat' : undefined}
        backgroundPosition={isLargeScreen ? 'center' : undefined}
      >
        {hasInfo && (
          <Stack
            w="100%"
            overflow="scroll"
            spacing={6}
            px={12}
            py={isLargeScreen ? '120px' : '0'}
          >
            <Text fontWeight="bold">お知らせ</Text>
            {informationList.map(info => (
              <Alert
                status="info"
                key={info.uid}
                overflow="scroll"
                minH="120px"
              >
                <AlertIcon />
                <Box>
                  <AlertTitle>{info.title}</AlertTitle>
                  <AlertDescription whiteSpace="pre-wrap">
                    <Linkify>{info.content}</Linkify>
                  </AlertDescription>
                </Box>
              </Alert>
            ))}
          </Stack>
        )}
      </Box>

      <Text position="absolute" bottom="4" left="4">
        {`ver.${currentVersion}`}
      </Text>

      <SSOAccountActivatedDialog
        isOpen={isSSOAccountActivatedDialogOpen}
        onClose={onCloseSSOAccountActivatedDialog}
      />
    </Flex>
  )
}
